// YERİ: src/app/domains/website/core/web-cms.service.ts
// melbarrel.com'un içerik kaynağı. Tüm uçlar ANONİM (/api/web/*).
//
// SSR notu: bu servisin çağrıları sunucuda da çalışır. Göreli /api'yi mutlak
// yapan yer ServerApiBackend'tir (core/http/api-origin.ts) — bir interceptor
// DEĞİL, bilerek: transfer cache interceptor'ı zincirde bizden sonra çalışır ve
// yanıtı gördüğü URL ile anahtarlar. Interceptor'da mutlaklaştırsaydık sunucu
// mutlak, tarayıcı göreli URL ile arar, anahtar ıskalanır ve tarayıcı HTML'e
// gömülmüş içeriği ağdan İKİNCİ kez çekerdi. (Tam olarak bu oldu.)

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';
import { API_BASE } from '@/app/core/auth/auth.service';
import {
  WebBlogPost,
  WebBlogSummary,
  WebLanguage,
  WebLeadRequest,
  WebLegalDocument,
  WebPage,
  WebPageSummary,
  WebDiscount,
  WebPricing,
  WebSection,
  WebServiceCatalog,
} from './web.models';

interface Envelope<T> {
  data: T;
  success: boolean;
  message: string;
}

interface Paged<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class WebCmsService {
  private http = inject(HttpClient);

  private readonly base = `${API_BASE}/web`;

  getLanguages(): Observable<WebLanguage[]> {
    return this.http
      .get<Envelope<WebLanguage[]>>(`${this.base}/languages`)
      .pipe(map((r) => r.data ?? []));
  }

  /**
   * Fiyat tablosu — paketler veritabanından.
   *
   * currency boş geçilebilir: backend varsayılan para birimine düşer. Kapalı
   * bir kod gelirse de varsayılana düşer, hata dönmez — eski bir bağlantıyla
   * gelen ziyaretçi fiyatsız sayfa görmesin.
   */
  getPricing(currency?: string): Observable<WebPricing | null> {
    return this.http
      .get<Envelope<WebPricing>>(`${this.base}/pricing`, {
        params: currency ? { currency } : {},
      })
      .pipe(map((r) => r.data ?? null));
  }

  /**
   * Vitrindeki kampanya rozetleri.
   *
   * Cografya AYRI verilir: ziyaretcinin ulkesi/bolgesi bilinmiyorsa yalniz
   * her yerde gecerli kampanyalar doner. Fiyat cagrisiyla birlestirmedik —
   * kampanya cekilemezse fiyatlar yine gorunmeli.
   */
  getPublicDiscounts(country?: string, region?: string): Observable<WebDiscount[]> {
    const params: Record<string, string> = {};
    if (country) params['country'] = country;
    if (region) params['region'] = region;
    return this.http
      .get<Envelope<WebDiscount[]>>(`${this.base}/pricing/discounts`, { params })
      .pipe(map((r) => r.data ?? []));
  }

  /**
   * Hizmet kataloğu — yayındaki hizmetler veritabanından.
   *
   * Katalog bir dönem CMS içinde elle yazılıydı ve hizmetler pasifleştirilince
   * sessizce eskidi: satılamayan kalemleri listeliyor, yenilerini göstermiyordu.
   *
   * lang boş geçilebilir: backend İngilizceye düşer. Bir kalem istenen dile
   * çevrilmemişse İngilizcesi, o da yoksa hizmetin kendi adı döner — kalem
   * hiçbir durumda kaybolmaz.
   */
  getServiceCatalog(lang?: string): Observable<WebServiceCatalog | null> {
    // Dil başına TEK istek. Katalog artık yalnız /services'te değil, hizmet
    // kartını taşıyan 33 sayfada okunuyor (ana sayfa + fiyatlar + 31 sektör);
    // önbelleksiz, sektörler arasında gezinen bir ziyaretçi aynı listeyi
    // onlarca kez çekerdi. İçerik bir istek boyunca değişmez, tenant'a göre
    // farklılaşmaz — paylaşmak güvenli.
    const key = lang || '';
    let cached = this.catalogCache.get(key);

    if (!cached) {
      cached = this.http
        .get<Envelope<WebServiceCatalog>>(`${this.base}/service-catalog`, {
          params: lang ? { lang } : {},
        })
        .pipe(
          map((r) => r.data ?? null),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
      this.catalogCache.set(key, cached);
    }
    return cached;
  }

  /** Dil kodu → katalog. SSR'da da dolar; sunucu süreci uzun ömürlü olduğu için
   *  burada sınırsız büyümez: anahtar sayısı yayındaki dil sayısı kadardır. */
  private catalogCache = new Map<string, Observable<WebServiceCatalog | null>>();

  /** Sitenin çerçevesi (üst menü + alt bilgi). Master yoksa boş liste döner. */
  getMasterSections(lang: string): Observable<WebSection[]> {
    return this.http
      .get<Envelope<WebSection[]>>(`${this.base}/master`, { params: { lang } })
      .pipe(map((r) => r.data ?? []));
  }

  getPages(lang: string): Observable<WebPageSummary[]> {
    return this.http
      .get<Envelope<WebPageSummary[]>>(`${this.base}/pages`, { params: { lang } })
      .pipe(map((r) => r.data ?? []));
  }

  /**
   * Tek sayfa. Slug ÇOK SEGMENTLİ olabilir ("solutions/restaurant") — backend
   * catch-all bekler, o yüzden segmentleri encode edip '/' ile birleştiriyoruz
   * (encodeURIComponent tek başına '/'yi de kaçırırdı).
   *
   * İstenen dilde sayfa yoksa 404 döner — çağıran 404 sayfasını gösterir.
   */
  getPage(slug: string, lang: string): Observable<WebPage> {
    const path = slug.split('/').map(encodeURIComponent).join('/');
    return this.http
      .get<Envelope<WebPage>>(`${this.base}/pages/${path}`, { params: { lang } })
      .pipe(map((r) => r.data));
  }

  getLegalDocuments(lang: string): Observable<WebLegalDocument[]> {
    return this.http
      .get<Envelope<WebLegalDocument[]>>(`${this.base}/legal`, { params: { lang } })
      .pipe(map((r) => r.data ?? []));
  }

  getLegalDocument(code: string, lang: string): Observable<WebLegalDocument> {
    return this.http
      .get<Envelope<WebLegalDocument>>(`${this.base}/legal/${encodeURIComponent(code)}`, {
        params: { lang },
      })
      .pipe(map((r) => r.data));
  }

  getBlogPosts(page = 1, pageSize = 9, categorySlug?: string): Observable<Paged<WebBlogSummary>> {
    const params: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
    if (categorySlug) params['categorySlug'] = categorySlug;
    return this.http.get<Paged<WebBlogSummary>>(`${this.base}/blog`, { params });
  }

  getBlogPost(slug: string): Observable<WebBlogPost> {
    return this.http
      .get<Envelope<WebBlogPost>>(`${this.base}/blog/${encodeURIComponent(slug)}`)
      .pipe(map((r) => r.data));
  }

  submitLead(request: WebLeadRequest): Observable<Envelope<unknown>> {
    return this.http.post<Envelope<unknown>>(`${this.base}/leads`, request);
  }
}
